import React, { useState, useEffect } from "react";
import { Typography, Box } from '@mui/material';
import PickStatsCardSingle from "./PickStatsCard/PickStatsCardSingle";
import { useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { getTeamLogo, getTeamNameShort } from "../../../utils/SportsData";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { DateTime } from 'luxon';
import IntervalDropdown from "./IntervalDropdown";

const GameStatsNew = ({ filteredDatesReverse }) => {
    const {
        league,
        interval,
        leaderboardData,
    } = useUserAndGameContext();

    const {
        gameSchedule,
        teams,
    } = useSuperPickemContext();
    //const interval = "2025-04-19T00:00:00"

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [filteredSchedule, setFilteredSchedule] = useState([]);

    useEffect(() => {
        if (interval && gameSchedule) {
            // Convert string to DateTime object if needed
            const intervalDate = typeof interval === 'string' 
                ? DateTime.fromISO(interval) 
                : interval;
            
            const formattedInterval = intervalDate.toFormat("yyyy-MM-dd'T'HH:mm:ss");
            
            const filteredGames = gameSchedule.filter(game => 
                game.Day === formattedInterval && game.Status !== "NotNecessary"
            );
            
            // Sort filtered games like in the original component
            filteredGames.sort((a, b) => {
                // Define the desired order for Status
                const statusOrder = ['InProgress', 'Final', 'F/OT'];
            
                // Normalize statuses: Treat 'Final' and 'F/OT' as equivalent
                const normalizeStatus = (status) => (status === 'F/OT' ? 'Final' : status);
            
                // Get the normalized index of each game's Status in the statusOrder array
                const statusA = statusOrder.indexOf(normalizeStatus(a.Status));
                const statusB = statusOrder.indexOf(normalizeStatus(b.Status));
            
                // Handle Status order first
                if (statusA !== statusB) {
                    // If statusA or statusB isn't in the array, place them at the end (use Infinity)
                    return (statusA === -1 ? Infinity : statusA) - (statusB === -1 ? Infinity : statusB);
                }
            
                // If Status is 'Final' or 'F/OT', sort by reverse DateTime (most recent first)
                if (normalizeStatus(a.Status) === 'Final') {
                    return new Date(b.DateTime) - new Date(a.DateTime); // Reverse order for completed games
                }
            
                // For other statuses, sort by ascending DateTime
                return new Date(a.DateTime) - new Date(b.DateTime);
            });
            
            setFilteredSchedule(filteredGames);
        }
    }, [interval, gameSchedule]);
    
    // console.log("Filtered Schedule: ", filteredSchedule, gameSchedule);

    const getPickStatsPickInfo = (gameId, awayTeamId, homeTeamId, leaderboard) => {
        const stats = {
            away: { count: 0, wins: 0, losses: 0, pushes: 0 },
            home: { count: 0, wins: 0, losses: 0, pushes: 0 }
        };
        
        leaderboard?.forEach(user => {
            // Assuming all picks are spread picks now
            const spreadPick = user?.picks?.find(pick => 
                pick?.gameId === gameId
            );
            
            if (spreadPick) {
                const side = spreadPick.teamId === awayTeamId ? 'away' : 'home';
                stats[side].count++;
                
                if (spreadPick.result === "win") stats[side].wins++;
                else if (spreadPick.result === "loss") stats[side].losses++;
                else if (spreadPick.result === "push") stats[side].pushes++;
            }
        });
        
        // Helper function to determine result (win, loss, push, or Split)
        const getResult = (wins, losses, pushes) => {
            const nonZeroCount = [wins, losses, pushes].filter(count => count > 0).length;
            if (nonZeroCount > 1) return "Split";  // More than one non-zero value means "Split"
            if (wins > 0) return "Win";
            if (losses > 0) return "Loss";
            if (pushes > 0) return "Push";
            return "No Result";  // If none have a value > 0, return "No Result"
        };
        
        return {
            awayCount: stats.away.count,
            awayWins: stats.away.wins,
            awayLosses: stats.away.losses,
            awayPushes: stats.away.pushes,
            awayResult: getResult(stats.away.wins, stats.away.losses, stats.away.pushes),
            
            homeCount: stats.home.count,
            homeWins: stats.home.wins,
            homeLosses: stats.home.losses,
            homePushes: stats.home.pushes,
            homeResult: getResult(stats.home.wins, stats.home.losses, stats.home.pushes)
        };
    };

    // Helper function to get game info
    const getPickStatsGameInfo = (gameId, schedule, teams) => {
        const game = schedule.find(g => g.GlobalGameID === gameId);
        if (!game) return null;
        
        const awayTeamId = game.GlobalAwayTeamID;
        const homeTeamId = game.GlobalHomeTeamID;
        const timeNowInNewYork = DateTime.now().setZone('America/New_York');
        const gameDateTime = game?.DateTime;
        const gameDateTimeInNewYork = DateTime.fromISO(gameDateTime, { zone: 'America/New_York' });
        
        return {
            gameId: gameId,
            awayTeamId: awayTeamId,
            homeTeamId: homeTeamId,
            awayTeamName: getTeamNameShort(league, awayTeamId, teams),
            homeTeamName: getTeamNameShort(league, homeTeamId, teams),
            awayTeamLogo: getTeamLogo(league, awayTeamId, teams),
            homeTeamLogo: getTeamLogo(league, homeTeamId, teams),
            awayTeamScore: game.AwayTeamScore ?? game.AwayScore,
            homeTeamScore: game.HomeTeamScore ?? game.HomeScore,
            status: game.Status,
            dateTime: game.DateTime,
            hasGameStarted: timeNowInNewYork > gameDateTimeInNewYork,
            homePointSpread: game.PointSpread || null,
        };
    };

    // Current interval information
    const thisIntervalsName = filteredDatesReverse?.find(date => date.day === interval)?.name || "Current Games";

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0 16px',
                    marginBottom: '24px',
                }}
            >
                <IntervalDropdown 
                    filteredDatesReverse={filteredDatesReverse} 
                    currentInterval={interval}
                />
            </Box>

            {filteredSchedule?.length === 0 && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: isMobile ? '20px' : '24px',
                            fontWeight: '700',
                            marginTop: '24px',
                            padding: '0 16px',
                        }}
                    >
                       {thisIntervalsName} Game Stats will be <br /> revealed when the matchups are set.
                    </Typography>
                </Box>
            )}
        
            {filteredSchedule?.length > 0 && (  
                <Box>
                    {filteredSchedule.map(game => {
                        const gameId = game.GlobalGameID;
                        const gameInfo = getPickStatsGameInfo(gameId, filteredSchedule, teams);
                        
                        if (!gameInfo) return null;
                        
                        const pickInfo = getPickStatsPickInfo(
                            gameId, 
                            gameInfo.awayTeamId, 
                            gameInfo.homeTeamId, 
                            leaderboardData
                        );
                        
                        return (
                            <PickStatsCardSingle
                                key={gameId}
                                gameInfo={gameInfo}
                                pickInfo={pickInfo}
                                thisIntervalsName={thisIntervalsName}
                            />
                        );
                    })}
                </Box>
            )}
        </>
    );
};

export default GameStatsNew;